import { fromPairs, keyBy } from 'lodash';
import { Team } from 'api/models/core-types';
import { TeamId } from 'components/organisms/tasks/fields';
import {
  Empty,
  Loaded,
  Loading,
  RefreshableContent,
  Refreshing,
} from 'stores/core';

export type TeamsStore = {
  ids: RefreshableContent<number[]>;
  byId: Record<number, Team>;
};

const EMPTY: TeamsStore = {
  byId: {},
  ids: Empty(),
};

const load =
  () =>
  ({ byId, ids }: TeamsStore): TeamsStore => ({
    byId,
    ids:
      ids[0] === 'refreshing' || ids[0] === 'loading'
        ? ids
        : ids[0] === 'loaded'
        ? Refreshing(ids)
        : Loading(),
  });

const putAll = (teams: Team[]) => (): TeamsStore => {
  const ids = teams.map(({ teamId }) => teamId);
  const lookup = keyBy(teams, 'teamId') as Record<number, Team>;

  return {
    byId: fromPairs(ids.map((id) => [id, lookup[id]])),
    ids: Loaded(ids),
  };
};

const loaded = ({ ids }: TeamsStore) => ids[0] === 'loaded';

const toList = ({ ids: [, teams = []], byId }: TeamsStore) =>
  teams.map((x) => byId[x]).filter(Boolean);

const ofId =
  ([, teamId]: TeamId) =>
  ({ byId }: TeamsStore): Team | undefined =>
    byId[teamId];

const add =
  (team: Team) =>
  ({ ids, byId }: TeamsStore): TeamsStore => ({
    byId: { ...byId, [team.teamId]: team },
    ids: Loaded(ids[1] ? [...ids[1], team.teamId] : []),
  });

const remove =
  (teamId: number) =>
  ({ ids, byId }: TeamsStore): TeamsStore => {
    const { [teamId]: deletedTeam, ...newById } = byId;

    return {
      byId: { ...newById },
      ids: Loaded(ids[1] ? [...ids[1].filter((_id) => _id !== teamId)] : []),
    };
  };

const update =
  (team: Team) =>
  ({ ids, byId }: TeamsStore): TeamsStore => ({
    ids,
    byId: { ...byId, [team.teamId]: { ...byId[team.teamId], ...team } },
  });

export const TeamsStore = {
  empty: () => EMPTY,
  load,
  putAll,
  loaded,
  toList,
  ofId,
  add,
  remove,
  update,
};
